import { getMinimumAge } from '@wineryxplorer/shared';
import { Input } from '../ui/Input';
import { CountrySelect } from '../ui/CountrySelect';
import { FormSection } from '../ui/FormSection';
import type { OnboardingFormData, OnboardingFormErrors } from './types';

interface OnboardingStepLocationProps {
  formData: OnboardingFormData;
  errors: OnboardingFormErrors;
  disabled: boolean;
  onChange: (field: keyof OnboardingFormData, value: string) => void;
  onValidateBirthday: (birthdayValue: string, countryValue: string) => void;
  onValidateCity: (cityValue: string) => void;
  onValidateState: (stateValue: string) => void;
  onValidateCountry: (countryValue: string) => void;
}

/** Step 1 — birthdate and location. Validation results are owned by the page. */
export const OnboardingStepLocation = ({
  formData,
  errors,
  disabled,
  onChange,
  onValidateBirthday,
  onValidateCity,
  onValidateState,
  onValidateCountry,
}: OnboardingStepLocationProps) => {
  const minimumAge = formData.country ? getMinimumAge(formData.country) : 16;
  const helperText = formData.country
    ? `You must be at least ${minimumAge} years old${minimumAge === 21 ? ' in the US and Canada' : ''}`
    : 'You must be at least 21 years old in the US and Canada, 16 years old elsewhere';

  return (
    <FormSection
      title="Birthday & Location"
      description="We need your birthdate to confirm you are old enough, and your location to show nearby wineries."
      divider={false}
      className="mt-0"
    >
      <Input
        label="Birthdate"
        type="date"
        value={formData.birthday}
        onChange={(e) => {
          const newBirthday = e.target.value;
          onChange('birthday', newBirthday);
          onValidateBirthday(newBirthday, formData.country);
        }}
        error={errors.birthday}
        success={formData.birthday.length > 0 && !errors.birthday}
        helperText={helperText}
        fullWidth
        required
        disabled={disabled}
        max={new Date().toISOString().split('T')[0]}
      />

      <Input
        label="Street Address (optional)"
        type="text"
        name="street-address"
        autoComplete="street-address"
        value={formData.street}
        onChange={(e) => onChange('street', e.target.value)}
        placeholder="123 Main St"
        fullWidth
        disabled={disabled}
      />

      <Input
        label="City"
        type="text"
        name="city"
        autoComplete="address-level2"
        value={formData.city}
        onChange={(e) => {
          const newCity = e.target.value;
          onChange('city', newCity);
          onValidateCity(newCity);
        }}
        placeholder="San Francisco"
        error={errors.city}
        success={formData.city.trim().length > 0 && !errors.city}
        fullWidth
        required
        disabled={disabled}
      />

      <Input
        label="State/Province"
        type="text"
        name="state"
        autoComplete="address-level1"
        value={formData.state}
        onChange={(e) => {
          const newState = e.target.value;
          onChange('state', newState);
          onValidateState(newState);
        }}
        placeholder="California"
        error={errors.state}
        success={formData.state.trim().length > 0 && !errors.state}
        fullWidth
        required
        disabled={disabled}
      />

      <Input
        label="Postal Code (optional)"
        type="text"
        name="postal-code"
        autoComplete="postal-code"
        value={formData.postalCode}
        onChange={(e) => onChange('postalCode', e.target.value)}
        placeholder="94102"
        fullWidth
        disabled={disabled}
      />

      <CountrySelect
        label="Country"
        value={formData.country}
        onChange={(newCountry) => {
          onChange('country', newCountry);
          onValidateCountry(newCountry);
          if (formData.birthday) {
            onValidateBirthday(formData.birthday, newCountry);
          }
        }}
        placeholder="Start typing a country..."
        error={errors.country}
        success={formData.country.trim().length > 0 && !errors.country}
        fullWidth
        required
        disabled={disabled}
      />
    </FormSection>
  );
};
